import { ShieldCheck, ShieldOff } from "lucide-react";
import { useEffect, useState } from "react";

import {
  classifyFinalQuestioningWriteFailure,
  useFinalQuestioningQuery,
  useReplaceFinalQuestioningMutation,
  type FinalQuestioningWriteFailure,
  type ProjectFinalQuestioning,
} from "@/api/finalQuestioning";
import { useCapability } from "@/auth/SessionContext";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import {
  useFinalQuestioningStore,
  useRejectedFinalQuestioningTokens,
} from "@/state/finalQuestioningState";
import { Notice } from "@/ui/Notice";
import { Tooltip } from "@/ui/Tooltip";

import { FinalQuestioningWarning } from "./FinalQuestioningWarning";

/**
 * Final safety questioning — the Project Safety control for the scoped S7
 * exception (#853, over #851's action).
 *
 * ## WHAT IT CORRECTS ON THE REFERENCE SCREEN
 *
 * The [Lovable Safety
 * screen](https://noproductcontent.lovable.app/projects/mod/config/safety) is
 * adopted safety-path rows under a top-right adoption action, with ONE switch
 * above them: *Enable runtime Safety* — "when off, safety paths are configured
 * here but not enforced at runtime". That switch conflates two independent
 * mechanisms and can express neither:
 *
 *  * the FINAL safety questioning stage the interview runs at the end, which is
 *    a Project's own governed clinical choice and the one thing S7 authorizes a
 *    Project to turn off; and
 *  * the deployment target's PER-MESSAGE runtime safety detection, which the
 *    Editor neither authors nor observes and which no control here may imply it
 *    can change.
 *
 * So this control is named for exactly the first, and the second is displayed
 * separately as read-only, target-derived state where a preview is actually
 * launched (`ui/preview/RuntimeSafetyDetection`). The rows below, the adoption
 * action and the screen's arrangement are unchanged; this is the explicit
 * addition the ticket authorizes, in `ProjectSectionPane`'s `controls` slot —
 * the insertion point #909 declared, so nothing about `SectionSurface` or the
 * Safety body is restructured to make room for it.
 *
 * ## THE THREE THINGS THE ACTION OWES, AND WHERE EACH IS MET
 *
 * 1. **Consequences, shown before confirming.** The list is the SERVER's
 *    (`consequences`), rendered verbatim and in order, and the digest the
 *    client echoes is the one it displayed. A deployment that has revised the
 *    wording refuses the write rather than recording a confirmation of text
 *    nobody read.
 * 2. **Explicit confirmation, and cancel changes nothing.** Disabling is a
 *    second step behind a checkbox, and Cancel sends no request at all — there
 *    is no "no change" write, and no state is inferred from a partial call.
 * 3. **Bound to the revision it was given against.** The three tokens travel
 *    from the read that produced them — contract revision, state version, and
 *    the draft version PLUS generation — captured when the dialog opened and
 *    never re-read at submit time. Refreshing a token behind the clinician is
 *    how a stale refusal becomes a lost update.
 *
 * ## WHAT IT WILL NOT OFFER
 *
 * No control here reaches safety wording, a safety path document, or the
 * target's detector policy: none of them is representable in the request. The
 * governed region is named in CLINICAL words rather than as the server's
 * `governedFlowPaths` — #488's payload boundary forbids a dotted projection
 * address anywhere on a Project route, and `projectPayloadBoundary.test.ts`
 * enforces it; what a clinical author needs is which decision is locked, not
 * its address. Re-enabling and resetting are always offered to a holder,
 * without a confirmation step — restoring the safety stage is the recovery
 * direction, and gating it would be the one refusal that leaves a Project
 * stuck off.
 */
export function FinalQuestioningControl({ projectId }: { projectId: string }) {
  const { query, state, configure, awaitingRefresh, openDialog, dialog } =
    useFinalQuestioningDialog(projectId);

  return (
    <div className="space-y-3">
      {/*
        The Safety surface's own copy of §8's persistent warning, above the
        control rather than inside it: it states the Project's posture, which
        is true whether or not this account may change it, and it is the same
        component the header, validation and preview render.
      */}
      <FinalQuestioningWarning projectId={projectId} surface="safety" />

      {state === null ? (
        <p
          className="rounded-md border border-border bg-card px-5 py-4 text-sm text-muted-foreground"
          data-slot="final-questioning"
        >
          {query.isPending
            ? "Reading this project's final safety questioning…"
            : "This project's final safety questioning could not be read. The safety paths below are unaffected."}
        </p>
      ) : (
        <div
          data-slot="final-questioning"
          className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-border bg-card px-5 py-4"
        >
          <div className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-foreground">
                Final safety questioning
              </span>
              {/*
                Icon AND text, never the tone alone: the badge's own words say
                on or off, and the glyph beside them is decorative. A reader
                who cannot see the colour reads the same state.
              */}
              <Badge tone={state.effective === "disabled" ? "warning" : "outline"}>
                {state.effective === "disabled" ? (
                  <ShieldOff aria-hidden="true" className="me-1 h-3 w-3" />
                ) : (
                  <ShieldCheck aria-hidden="true" className="me-1 h-3 w-3" />
                )}
                {stateLabel(state)}
              </Badge>
            </span>
            <p className="mt-0.5 text-sm text-muted-foreground">
              {describe(state)}
            </p>
          </div>

          <div className="relative z-10 flex shrink-0 items-center gap-1">
            <Tooltip
              content={
                awaitingRefresh
                  ? "The Editor Service refused the last change, so this project's final safety questioning is being read again. It opens once that read lands."
                  : configure.allowed
                    ? "Configure final safety questioning"
                    : "View final safety questioning"
              }
            >
              <Button
                variant="outline"
                size="sm"
                // Refused rather than hidden, and `Button` keeps an
                // `aria-disabled` control in the tab order so its reason stays
                // reachable — the one place this surface will not open is on
                // tokens it already knows the server rejects.
                disabled={awaitingRefresh}
                onClick={() => {
                  openDialog();
                }}
              >
                {/* One control whatever the account may do. Naming it
                    "Configure" for a reader would promise a write they do not
                    hold; the dialog states the refusal in words. */}
                {configure.allowed ? "Configure" : "View"}
              </Button>
            </Tooltip>
          </div>

          {/*
            MOUNTED while it is open, whatever `awaitingRefresh` says. The
            dialog is what CARRIES the refusal notice, so unmounting it the
            instant it reports its rejected tokens would take the explanation
            with it and leave a control that silently declines to open. The
            guard belongs on the next OPEN, not on this one.
          */}
          {dialog}
        </div>
      )}
    </div>
  );
}

/** The switch only opens the confirmation dialog; it never writes by itself. */
export function FinalQuestioningSwitchRow({ projectId }: { projectId: string }) {
  const { query, state, configure, awaitingRefresh, openDialog, dialog } =
    useFinalQuestioningDialog(projectId);

  if (state === null) {
    return (
      <p
        data-slot="final-questioning-switch"
        className="text-sm text-muted-foreground"
      >
        {query.isPending
          ? "Reading this project's final safety questions…"
          : "This project's final safety questions could not be read."}
      </p>
    );
  }
  const on = state.effective !== "disabled";
  return (
    <div
      data-slot="final-questioning-switch"
      className="flex flex-wrap items-center gap-x-3 gap-y-1"
    >
      <div className="min-w-0 flex-1">
        <span className="text-sm font-medium text-foreground">
          Final safety questions
        </span>
        <p className="text-xs text-muted-foreground">
          {on
            ? "The interview asks its final safety questions at the end."
            : "The interview does not ask its final safety questions at the end."}{" "}
          {configure.allowed
            ? "Changing this asks you to confirm first."
            : "Shown for reference: this account may not change it."}
        </p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label="Final safety questions"
        aria-disabled={awaitingRefresh || undefined}
        onClick={openDialog}
        className={
          "relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border border-input bg-secondary transition-colors " +
          "aria-checked:border-primary aria-checked:bg-primary " +
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring " +
          "aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
        }
      >
        <span
          aria-hidden="true"
          className={
            "pointer-events-none ml-0.5 h-3.5 w-3.5 rounded-full bg-card shadow transition-transform " +
            (on ? "translate-x-4" : "translate-x-0")
          }
        />
      </button>
      {dialog}
    </div>
  );
}

/** The read, dialog state and reopen barrier shared by the two places that open this confirmation. */
function useFinalQuestioningDialog(projectId: string) {
  const query = useFinalQuestioningQuery(projectId);
  const configure = useCapability("final_questioning.configure");
  /**
   * WHICH project the dialog was opened for, not merely whether one is open.
   *
   * This control is reconciled rather than remounted when a clinician moves
   * between two Projects' Safety pages, so a boolean would leave an open
   * dialog standing over the NEW project while its pinned tokens still named
   * the old one — and the mutation hook, which takes the live `projectId`,
   * would send those tokens to the new project's URL. Two untouched Projects
   * share every coordinate a compare-and-set checks, so that write would be
   * ACCEPTED: a disable recorded against a Project nobody chose (claude review
   * round 4, P1).
   *
   * Comparing the id closes it by construction — no effect, no cleanup, and no
   * window between the prop changing and a guard noticing.
   *
   * The comparison ALONE was not enough, though: it hides the dialog on A → B
   * and leaves `openedFor` naming A, so returning B → A through the same
   * reconciled component made `open` true again and resurrected a modal
   * nobody had asked for, over a page the clinician had only just reached
   * (claude review round 5). The stale opener is dropped during render —
   * React's documented adjust-state-on-a-changed-prop form, and the same one
   * `ProjectPreviewDialog` uses to retire a withdrawn patient choice. In
   * render rather than an effect because an effect runs a commit later, which
   * is a frame with a resurrected dialog in it; and it is this component
   * setting its OWN state, not the cross-component update round 3 removed.
   */
  const [openedFor, setOpenedFor] = useState<string | null>(null);
  if (openedFor !== null && openedFor !== projectId) {
    setOpenedFor(null);
  }
  // The identity check STAYS, and is not made redundant by the reset above:
  // React restarts this render with the cleared value, and until it does this
  // is what keeps a dialog from being open for the wrong project.
  const open = openedFor === projectId;
  /**
   * The token tuple the service last REJECTED for this project, or null.
   *
   * The refusal invalidates the read, but an invalidation is not a barrier:
   * React Query keeps serving the rejected entry while it refetches, so a
   * clinician who follows the notice's "close and reopen" immediately can
   * capture the very tokens that were just refused and be refused again
   * (claude review round 2, P2).
   *
   * Compared as a TUPLE rather than timed. A deadline — `isFetching`, a
   * timestamp — is a race with a shorter window, not one less race; comparing
   * what the read actually says is a fact. Every refusal this can produce
   * moves one of its coordinates by construction, so the moment the
   * authoritative read lands the tuple differs and the control opens again on
   * tokens the server would take.
   *
   * It lives in a STORE and not here because this control unmounts: Safety →
   * another section → Safety builds a new one, while the Project header's own
   * copy of the query is still serving the rejected value (claude review round
   * 4, P2). A barrier that a navigation could step around is not one.
   */
  const rejected = useRejectedFinalQuestioningTokens(projectId);
  const setRejected = useFinalQuestioningStore((store) => store.reject);
  const retireRejected = useFinalQuestioningStore((store) => store.retire);

  const state = query.data ?? null;
  const tokens = state === null ? null : tokenIdentity(projectId, state);
  const awaitingRefresh = rejected !== null && tokens === rejected;

  /**
   * Retire the barrier once an authoritative read has moved past it.
   *
   * The comparison above is what makes the guard CORRECT — it lifts the moment
   * the read differs, with no effect involved. This only garbage-collects the
   * entry, which matters because the tuple is not one-way: the consequence
   * wording can change without a revision bump, so a rollout and a rollback
   * can bring the refused tuple back and re-arm a barrier for a refusal that
   * is long over (claude review round 5).
   *
   * An effect is the right place for exactly that reason — nothing on screen
   * waits for it — and it is a store write, which must not happen during
   * render.
   */
  useEffect(() => {
    if (rejected !== null && tokens !== null && tokens !== rejected) {
      retireRejected(projectId);
    }
  }, [rejected, tokens, projectId, retireRejected]);

  const dialog =
    open && state !== null ? (
      <FinalQuestioningDialog
        projectId={projectId}
        state={state}
        canConfigure={configure.allowed}
        refusal={configure.reason}
        onRejected={(refused) => setRejected(projectId, refused)}
        onClose={() => setOpenedFor(null)}
      />
    ) : null;

  return {
    query,
    state,
    configure,
    awaitingRefresh,
    openDialog: () => {
      if (!awaitingRefresh) setOpenedFor(projectId);
    },
    dialog,
  };
}

/**
 * `inherited` and `enabled` are the same behaviour and different decisions, and
 * the label says which: NII must be able to state that it KEEPS final safety
 * questioning, and a Project that has never decided must not be shown as having
 * decided.
 */
function stateLabel(state: ProjectFinalQuestioning): string {
  if (state.state === "disabled") {
    return "Off";
  }
  return state.state === "enabled"
    ? "On — set for this project"
    : "On — inherited from the base pack";
}

function describe(state: ProjectFinalQuestioning): string {
  if (state.effective === "disabled") {
    return "The interview does not run its final safety questioning stage for this project. Safety wording and the safety paths below are unchanged.";
  }
  return state.state === "enabled"
    ? "The interview asks its final safety questions at the end of every session. This project has explicitly chosen to keep them."
    : "The interview asks its final safety questions at the end of every session, from the base pack. This project has not changed that.";
}

/**
 * Everything a write is bound to, as one comparable value.
 *
 * Not rendered and not a digest of its own: it exists so "the read has not
 * moved since the service rejected these" is a comparison rather than a timer.
 *
 * THE PROJECT ID IS PART OF IT, and leaving it out was a real defect (claude
 * review round 3): this control keeps its state across a navigation between
 * two Projects, and `v1|0|dv-initial|0` is the tuple EVERY untouched Project
 * has — so a refusal on one would have disabled the control on the next one
 * indefinitely, since an untouched Project's tuple never moves.
 *
 * THE WORDING DIGEST IS PART OF IT for the case the contract revision cannot
 * express: a deployment may edit the consequences without bumping the label,
 * and the service compares the digest first precisely so that edit cannot pass
 * as confirmed. The refusal it produces is bound to the digest, so the
 * recovery has to be too.
 */
function tokenIdentity(
  projectId: string,
  state: ProjectFinalQuestioning,
): string {
  return [
    projectId,
    state.contractRevision,
    state.consequencesDigest,
    state.stateVersion,
    state.currentDraftVersion,
    state.currentDraftGeneration,
  ].join("|");
}

/**
 * A refusal that disproves the tokens this dialog holds, and is therefore
 * terminal FOR IT: every further attempt from here would either fail
 * identically or, if the tokens were refreshed underneath, bind a confirmation
 * to a revision the clinician never saw.
 *
 * Named once because two callers ask it — the render, to withhold the write
 * controls, and the mutation's own `onError`, to arm the parent's reopen
 * guard — and two spellings of "terminal" would be two chances to disagree
 * about whether reopening is safe.
 */
function isTerminalRefusal(failure: FinalQuestioningWriteFailure): boolean {
  return (
    failure.kind === "stale_state" ||
    failure.kind === "stale_draft" ||
    failure.kind === "contract_revised"
  );
}

type Step = "overview" | "confirm-disable";

function FinalQuestioningDialog({
  projectId,
  state,
  canConfigure,
  refusal,
  onRejected,
  onClose,
}: {
  projectId: string;
  state: ProjectFinalQuestioning;
  canConfigure: boolean;
  refusal: string | null;
  /** Hands the parent the token tuple the service refused, so a reopen on the
   *  same one is impossible rather than merely unlikely. */
  onRejected: (tokens: string) => void;
  onClose: () => void;
}) {
  const mutation = useReplaceFinalQuestioningMutation(projectId);
  /**
   * THE TOKENS THIS DIALOG OPENED ON, captured once and never re-read.
   *
   * `state` is the live query and a stale refusal REFETCHES it, so reading the
   * tokens off the prop at submit time would hand the loser the winner's fresh
   * tokens and let a second Confirm silently overwrite the concurrent decision
   * — the lost update the compare-and-set exists to refuse, reintroduced above
   * it (`ResponseFilteringControl` records the same trap). Pinning them makes a
   * second attempt fail exactly as the first did, until the clinician reopens
   * on what the Project now says.
   */
  const [opened] = useState(() => ({
    tokens: tokenIdentity(projectId, state),
    contractRevision: state.contractRevision,
    stateVersion: state.stateVersion,
    draftVersion: state.currentDraftVersion,
    draftGeneration: state.currentDraftGeneration,
    consequencesDigest: state.consequencesDigest,
    consequences: state.consequences,
  }));
  const [step, setStep] = useState<Step>("overview");
  const [acknowledged, setAcknowledged] = useState(false);

  const busy = mutation.isPending;
  const failure = mutation.isError
    ? classifyFinalQuestioningWriteFailure(mutation.error)
    : null;
  /**
   * A refusal that disproves the tokens this dialog holds is TERMINAL for it.
   * Every further attempt from here would either fail identically or — if the
   * tokens were refreshed — bind a confirmation to a revision the clinician
   * never saw. The write controls go, the notice explains, and Close is the
   * way out.
   */
  const terminal = failure !== null && isTerminalRefusal(failure);

  const writable = canConfigure && !terminal;
  const off = state.effective === "disabled";
  const stale = state.acknowledgment?.status === "stale";

  function submit(next: "inherited" | "enabled" | "disabled") {
    mutation.mutate(
      {
        expectedContractRevision: opened.contractRevision,
        expectedStateVersion: opened.stateVersion,
        expectedDraftVersion: opened.draftVersion,
        expectedDraftGeneration: opened.draftGeneration,
        state: next,
        ...(next === "disabled"
          ? {
              acknowledgment: {
                confirmed: true as const,
                // The digest of the wording THIS dialog displayed, not the
                // live one: that is what makes the record an acknowledgment
                // of something a person actually read.
                consequencesDigest: opened.consequencesDigest,
              },
            }
          : {}),
      },
      {
        onSuccess: onClose,
        /*
         * Tell the parent WHICH tokens were refused, from the mutation
         * callback rather than from render.
         *
         * The first version did it in the child's render body, which is the
         * "Cannot update a component while rendering a different component"
         * React forbids (claude review round 3) — and it was fragile beside
         * being wrong. This runs after the mutation settles and before the
         * clinician can reach the Close button, so the guard is armed by the
         * time a reopen is possible, without an extra commit.
         */
        onError(error) {
          if (isTerminalRefusal(classifyFinalQuestioningWriteFailure(error))) {
            onRejected(opened.tokens);
          }
        },
      },
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !busy) onClose();
      }}
    >
      <DialogContent
        busy={busy}
        className="max-w-2xl"
        title="Final safety questioning"
        description="The safety questions this project's interview asks at the end of a session. This is not the deployment target's per-message safety detection, which is its own policy and is not changed here."
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => {
                // CANCEL SENDS NOTHING. Not a "no change" write, not a reset:
                // an unsent request is the only cancel that provably changes
                // no state, and the server infers nothing from a partial call.
                //
                // A TERMINAL refusal skips the step back. Every notice above
                // one says "close and reopen", and stepping back would land on
                // an overview built from the tokens the server has just
                // rejected — a second click to reach the exit the copy names
                // (claude review, PR #968).
                if (step === "confirm-disable" && !terminal) {
                  setStep("overview");
                  setAcknowledged(false);
                  return;
                }
                onClose();
              }}
            >
              {step === "confirm-disable" && !terminal ? "Cancel" : "Close"}
            </Button>

            {step === "overview" && writable && off ? (
              <>
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() => submit("inherited")}
                >
                  Reset to the base pack
                </Button>
                <Button disabled={busy} onClick={() => submit("enabled")}>
                  Turn final safety questioning back on
                </Button>
              </>
            ) : null}

            {step === "overview" && writable && !off ? (
              <>
                {state.state === "enabled" ? (
                  <Button
                    variant="outline"
                    disabled={busy}
                    onClick={() => submit("inherited")}
                  >
                    Reset to the base pack
                  </Button>
                ) : null}
                <Button
                  variant="outline"
                  disabled={busy}
                  onClick={() => setStep("confirm-disable")}
                >
                  Turn off final safety questioning…
                </Button>
              </>
            ) : null}

            {step === "overview" && writable && off && stale ? (
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => setStep("confirm-disable")}
              >
                Confirm the consequences again…
              </Button>
            ) : null}

            {step === "confirm-disable" && writable ? (
              <Button
                disabled={!acknowledged || busy}
                onClick={() => submit("disabled")}
              >
                {off
                  ? "Confirm again and keep it off"
                  : "Turn off final safety questioning"}
              </Button>
            ) : null}
          </div>
        }
      >
        <div className="space-y-4 text-sm">
          {failure ? (
            <Notice tone="destructive" role="alert">
              {refusalSentence(failure)}
            </Notice>
          ) : null}

          {canConfigure ? null : (
            <Notice tone="warning">
              {refusal ??
                "This account may not change this project's final safety questioning."}{" "}
              The state below is shown so the posture is visible to everyone who
              can see the project.
            </Notice>
          )}

          {step === "overview" ? (
            <Overview state={state} />
          ) : (
            <ConfirmDisable
              consequences={opened.consequences}
              acknowledged={acknowledged}
              onAcknowledge={setAcknowledged}
              disabled={!writable || busy}
              renewing={off}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Overview({ state }: { state: ProjectFinalQuestioning }) {
  const acknowledgment = state.acknowledgment;
  return (
    <>
      <p className="text-muted-foreground">
        This project:{" "}
        <strong className="text-foreground">{stateLabel(state)}</strong>.{" "}
        {describe(state)}
      </p>

      {acknowledgment ? (
        <div className="rounded-md border border-border px-4 py-3">
          <h3 className="text-sm font-semibold text-foreground">
            The confirmation on record
          </h3>
          <p className="mt-1 text-muted-foreground">
            Confirmed by{" "}
            <code className="rounded bg-secondary px-1 font-mono text-xs">
              {acknowledgment.acknowledgedBySubject}
            </code>{" "}
            against draft revision{" "}
            <code className="rounded bg-secondary px-1 font-mono text-xs">
              {acknowledgment.draftVersion}
            </code>{" "}
            (generation {acknowledgment.draftGeneration}), consequences{" "}
            {acknowledgment.contractRevision}.
          </p>
          {acknowledgment.status === "stale" ? (
            // ACTIONABLE, not merely labelled: the two reasons have different
            // recoveries, and the freeze refuses on this same reading
            // (`safety.final_questioning_evidence_stale`), so a clinician has
            // to know which one to act on.
            <p className="mt-2 text-warning-foreground">
              {acknowledgment.staleReason === "contract_revised"
                ? "This deployment has revised the consequences since this confirmation was given, so it no longer covers the wording the server states. Read them again and confirm, or turn final safety questioning back on."
                : "The project's draft has moved since this confirmation was given, so it no longer covers what would be frozen. Read the consequences again and confirm, or turn final safety questioning back on."}{" "}
              Validation and freezing refuse until one of the two happens.
            </p>
          ) : null}
        </div>
      ) : null}

      <p className="text-muted-foreground">
        Whatever this project decides here, the safety wording and the safety
        path documents stay read-only, and the deployment target&rsquo;s own
        per-message safety detection is unchanged. This control writes one
        decision and nothing else — whether the interview runs its final safety
        questioning stage, and the order its final tracks run in — which is also
        why no other editor in this workspace can reach them.
      </p>
    </>
  );
}

function ConfirmDisable({
  consequences,
  acknowledged,
  onAcknowledge,
  disabled,
  renewing,
}: {
  consequences: string[];
  acknowledged: boolean;
  onAcknowledge: (next: boolean) => void;
  disabled: boolean;
  renewing: boolean;
}) {
  return (
    <>
      <p className="text-foreground">
        {renewing
          ? "Final safety questioning is already off for this project. Confirming again records a new confirmation against the project as it now stands; nothing else changes."
          : "Turning final safety questioning off changes what this project's interview does. Read what follows before confirming."}
      </p>

      {/*
        The SERVER's list, verbatim and in its order. Not paraphrased and not
        reordered: the digest the confirmation carries is taken over exactly
        these statements, so text written here instead would be a confirmation
        of something the server never said.
      */}
      <ol className="list-decimal space-y-2 rounded-md border border-warning/40 bg-warning/10 py-3 pe-4 ps-8">
        {consequences.map((consequence) => (
          <li key={consequence} className="text-foreground">
            {consequence}
          </li>
        ))}
      </ol>

      <label className="flex items-start gap-2 text-foreground">
        <input
          type="checkbox"
          className="mt-1"
          checked={acknowledged}
          disabled={disabled}
          onChange={(event) => onAcknowledge(event.target.checked)}
        />
        <span>
          I have read the consequences above and confirm this decision for this
          project.
        </span>
      </label>
    </>
  );
}

function refusalSentence(failure: FinalQuestioningWriteFailure): string {
  switch (failure.kind) {
    case "stale_state":
      return "This project's final safety questioning changed while this dialog was open, so nothing was saved. Close and reopen it to see the current state before deciding again.";
    case "stale_draft":
      return "The project's draft moved while this dialog was open, so a confirmation would have been bound to a revision that no longer exists. Nothing was saved. Close and reopen it, then decide again against the draft as it now stands.";
    case "contract_revised":
      return "This deployment states different consequences than the ones shown here, so nothing was saved. Close and reopen this control to read the wording the server actually states before confirming anything.";
    case "denied":
      return "This account may not change this project's final safety questioning. Nothing was changed.";
    case "invalid":
      return failure.message;
    case "ambiguous":
      return `The server did not answer this change${failure.requestId ? ` (request ${failure.requestId})` : ""}. Reopen this control to see whether it was applied before deciding again.`;
    default:
      return "The change was refused and nothing was saved.";
  }
}
